import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FiChevronDown,
  FiChevronLeft,
  FiChevronUp,
  FiHome,
  FiMenu,
  FiPhoneIncoming,
  FiSettings,
  FiUser,
} from "react-icons/fi";

import { TbFileReport } from "react-icons/tb";

import {
  DoorClosed,
  ParkingCircle,
  PersonStanding,
} from "lucide-react";

import {
  useLocation,
  useNavigate,
} from "react-router-dom";

import logo from "../../assets/logo8.png";

/**
 * PermissionManager component-д ашиглана.
 *
 * id нь заавал давхардахгүй байна.
 */
export const fullMenu = [
  {
    id: "dashboard",
    icon: <FiHome size={20} />,
    label: "Дашбоард",
    url: "/hrDashboard",
  },
  {
    id: "parking",
    icon: <ParkingCircle size={20} />,
    label: "Зогсоол",
    url: "/dashboard",
  },
  {
    id: "garage",
    icon: <DoorClosed size={20} />,
    label: "Гараж",
    url: "/garage-menu",
    groupOnly: true,
    subItems: [
      {
        id: "garage-control",
        label: "Хяналт",
        url: "/garage",
      },
      {
        id: "garage-register",
        label: "Бүртгэл",
        url: "/grageRegister",
      },
    ],
  },
  {
    id: "report",
    icon: <TbFileReport size={20} />,
    label: "Тайлан",
    url: "/report",
  },
  {
    id: "application",
    icon: <FiPhoneIncoming size={20} />,
    label: "App",
    url: "/app",
  },
  {
    id: "settings",
    icon: <FiSettings size={20} />,
    label: "Тохиргоо",
    url: "/settings",
  },
];

const ROLE_NAMES = {
  master: "Мастер",
  admin: "Администратор",
  operator: "Оператор",
  cass: "Касс",
};

/**
 * Sidebar доторх нэг submenu.
 */
const SubMenuItem = memo(function SubMenuItem({
  item,
  active,
  onClick,
}) {
  return (
    <li>
      <button
        type="button"
        onClick={(event) => onClick(event, item)}
        className={`
          tw-relative
          tw-flex
          tw-w-full
          tw-items-center
          tw-rounded-lg
          tw-px-3
          tw-py-2
          tw-text-left
          tw-text-sm
          tw-transition-colors
          ${
            active
              ? "tw-bg-slate-800 tw-font-medium tw-text-white"
              : "tw-text-slate-400 hover:tw-bg-slate-800/70 hover:tw-text-slate-200"
          }
        `}
      >
        {active && (
          <span className="tw-absolute tw-left-0 tw-h-5 tw-w-0.5 tw-rounded-r tw-bg-blue-500" />
        )}

        <span className="tw-truncate">
          {item.label}
        </span>
      </button>
    </li>
  );
});

/**
 * Sidebar доторх нэг үндсэн menu.
 */
const SidebarMenuItem = memo(function SidebarMenuItem({
  item,
  sidebarOpen,
  isOpen,
  isActive,
  activeSubUrl,
  onMenuClick,
  onSubMenuClick,
}) {
  const hasSubItems =
    Array.isArray(item.subItems) &&
    item.subItems.length > 0;

  return (
    <li>
      <button
        type="button"
        title={!sidebarOpen ? item.label : ""}
        aria-label={item.label}
        aria-expanded={
          hasSubItems ? isOpen : undefined
        }
        onClick={(event) =>
          onMenuClick(event, item)
        }
        className={`
          tw-relative
          tw-flex
          tw-w-full
          tw-items-center
          tw-rounded-lg
          tw-px-3
          tw-py-2.5
          tw-text-left
          tw-text-sm
          tw-font-medium
          tw-transition-colors
          ${
            sidebarOpen
              ? ""
              : "tw-justify-center"
          }
          ${
            isActive
              ? "tw-bg-slate-800 tw-text-white"
              : "tw-text-slate-400 hover:tw-bg-slate-800/70 hover:tw-text-slate-100"
          }
        `}
      >
        {isActive && (
          <span className="tw-absolute tw-left-0 tw-h-6 tw-w-1 tw-rounded-r tw-bg-blue-500" />
        )}

        <span
          className={`
            tw-flex
            tw-h-8
            tw-w-8
            tw-flex-shrink-0
            tw-items-center
            tw-justify-center
            ${
              isActive
                ? "tw-text-blue-400"
                : "tw-text-slate-500"
            }
          `}
        >
          {item.icon}
        </span>

        {sidebarOpen && (
          <>
            <span className="tw-ml-2 tw-min-w-0 tw-flex-1 tw-truncate">
              {item.label}
            </span>

            {hasSubItems && (
              <span className="tw-ml-2 tw-text-slate-500">
                {isOpen ? (
                  <FiChevronUp size={16} />
                ) : (
                  <FiChevronDown size={16} />
                )}
              </span>
            )}
          </>
        )}
      </button>

      {hasSubItems &&
        isOpen &&
        sidebarOpen && (
          <ul className="tw-ml-8 tw-mt-1 tw-space-y-1 tw-border-l tw-border-slate-800 tw-pl-3">
            {item.subItems.map((subItem) => (
              <SubMenuItem
                key={subItem.id || subItem.url}
                item={subItem}
                active={
                  activeSubUrl === subItem.url
                }
                onClick={onSubMenuClick}
              />
            ))}
          </ul>
        )}
    </li>
  );
});

const DashboardLayout = ({
  children,
  sidebarOpen,
  toggleSidebar,
  userRole = "operator",
  allowedMenus = [],
  userName = "Албан хаагч",
  organizationName = "iParking",
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const normalizedUserRole = useMemo(
    () =>
      String(userRole || "")
        .trim()
        .toLowerCase(),
    [userRole]
  );

  /**
   * Касс хэрэглэгчийн Зогсоол цэсийг төлбөрийн dashboard руу чиглүүлнэ.
   * fullMenu-ийн үндсэн /dashboard URL-ийг өөрчлөхгүй тул бусад role хэвийн үлдэнэ.
   */
  const resolveMenuUrl = useCallback(
    (item) => {
      if (
        normalizedUserRole === "cass" &&
        item?.id === "parking"
      ) {
        return "/paymentDashboard";
      }

      return item?.url || "";
    },
    [normalizedUserRole]
  );

  const [openMenus, setOpenMenus] =
    useState({});

  /**
   * Route идэвхтэй эсэхийг шалгана.
   */
  const isRouteActive = useCallback(
    (url) => {
      if (!url) return false;

      return (
        location.pathname === url ||
        location.pathname.startsWith(
          `${url}/`
        )
      );
    },
    [location.pathname]
  );

  /**
   * allowedMenus-ийг Set болгох нь
   * includes() олон дахин дуудахаас хурдан.
   */
  const allowedMenuSet = useMemo(() => {
    if (!Array.isArray(allowedMenus)) {
      return new Set();
    }

    return new Set(allowedMenus);
  }, [allowedMenus]);

  /**
   * Хэрэглэгчийн эрхээр цэс шүүнэ.
   */
  const filteredMenu = useMemo(() => {
    if (normalizedUserRole === "master") {
      return fullMenu;
    }

    if (allowedMenuSet.size === 0) {
      return [];
    }

    return fullMenu.reduce(
      (result, item) => {
        const resolvedItemUrl =
          resolveMenuUrl(item);

        if (
          Array.isArray(item.subItems)
        ) {
          const filteredSubItems =
            item.subItems.filter((subItem) =>
              allowedMenuSet.has(subItem.url)
            );

          const parentAllowed =
            allowedMenuSet.has(item.url) ||
            allowedMenuSet.has(
              resolvedItemUrl
            );

          if (
            parentAllowed ||
            filteredSubItems.length > 0
          ) {
            result.push({
              ...item,
              subItems: filteredSubItems,
            });
          }

          return result;
        }

        if (
          allowedMenuSet.has(item.url) ||
          allowedMenuSet.has(
            resolvedItemUrl
          )
        ) {
          result.push(item);
        }

        return result;
      },
      []
    );
  }, [
    allowedMenuSet,
    normalizedUserRole,
    resolveMenuUrl,
  ]);

  /**
   * Одоогийн active submenu.
   */
  const activeSubUrl = useMemo(() => {
    for (const item of filteredMenu) {
      const activeSubItem =
        item.subItems?.find((subItem) =>
          isRouteActive(subItem.url)
        );

      if (activeSubItem) {
        return activeSubItem.url;
      }
    }

    return "";
  }, [filteredMenu, isRouteActive]);

  /**
   * Идэвхтэй submenu байгаа үед
   * parent menu-г автоматаар нээнэ.
   */
  useEffect(() => {
    const activeParent =
      filteredMenu.find((item) =>
        item.subItems?.some((subItem) =>
          isRouteActive(subItem.url)
        )
      );

    if (!activeParent) return;

    setOpenMenus((previous) => {
      if (previous[activeParent.id]) {
        return previous;
      }

      return {
        ...previous,
        [activeParent.id]: true,
      };
    });
  }, [filteredMenu, isRouteActive]);

  const closeSidebarOnMobile =
    useCallback(() => {
      if (
        typeof window !== "undefined" &&
        window.innerWidth < 768 &&
        sidebarOpen
      ) {
        toggleSidebar();
      }
    }, [sidebarOpen, toggleSidebar]);

  const handleMenuClick = useCallback(
    (event, item) => {
      event.stopPropagation();

      const hasSubItems =
        Array.isArray(item.subItems) &&
        item.subItems.length > 0;

      if (hasSubItems) {
        if (!sidebarOpen) {
          toggleSidebar();

          setOpenMenus((previous) => ({
            ...previous,
            [item.id]: true,
          }));

          return;
        }

        setOpenMenus((previous) => ({
          ...previous,
          [item.id]:
            !previous[item.id],
        }));

        return;
      }

      const destinationUrl =
        resolveMenuUrl(item);

      if (destinationUrl) {
        navigate(destinationUrl);
        closeSidebarOnMobile();
      }
    },
    [
      closeSidebarOnMobile,
      navigate,
      resolveMenuUrl,
      sidebarOpen,
      toggleSidebar,
    ]
  );

  const handleSubMenuClick =
    useCallback(
      (event, subItem) => {
        event.stopPropagation();

        navigate(subItem.url);
        closeSidebarOnMobile();
      },
      [
        closeSidebarOnMobile,
        navigate,
      ]
    );

  /**
   * Гар утасны header-ийн нэр.
   */
  const currentPageName = useMemo(() => {
    for (const item of fullMenu) {
      const activeSubItem =
        item.subItems?.find((subItem) =>
          isRouteActive(subItem.url)
        );

      if (activeSubItem) {
        return activeSubItem.label;
      }
    }

    const activeMenu = fullMenu.find(
      (item) =>
        !item.groupOnly &&
        isRouteActive(
          resolveMenuUrl(item)
        )
    );

    return activeMenu?.label || "Систем";
  }, [isRouteActive, resolveMenuUrl]);

  const roleName =
    ROLE_NAMES[normalizedUserRole] ||
    "Хэрэглэгч";

  const userInitial = useMemo(() => {
    return String(userName || "А")
      .trim()
      .charAt(0)
      .toUpperCase();
  }, [userName]);

  return (
    <div className="tw-flex tw-h-screen tw-overflow-hidden tw-bg-slate-100 tw-font-sans tw-text-slate-900">
      {/* MOBILE OVERLAY */}
      {sidebarOpen && (
        <button
          type="button"
          aria-label="Цэс хаах"
          onClick={toggleSidebar}
          className="tw-fixed tw-inset-0 tw-z-40 tw-bg-slate-950/60 md:tw-hidden"
        />
      )}

      {/* SIDEBAR */}
      <aside
        className={`
          tw-fixed
          tw-inset-y-0
          tw-left-0
          tw-z-50
          tw-flex
          tw-flex-col
          tw-border-r
          tw-border-slate-800
          tw-bg-slate-900
          tw-shadow-xl
          tw-transition-[width,transform]
          tw-duration-300
          tw-ease-out
          md:tw-shadow-none
          ${
            sidebarOpen
              ? "tw-w-64 tw-translate-x-0"
              : "tw-w-64 tw--translate-x-full md:tw-w-20 md:tw-translate-x-0"
          }
        `}
      >
        {/* LOGO */}
        <div
          className={`
            tw-flex
            tw-h-16
            tw-flex-shrink-0
            tw-items-center
            tw-border-b
            tw-border-slate-800
            tw-px-4
            ${
              sidebarOpen
                ? "tw-justify-between"
                : "md:tw-justify-center"
            }
          `}
        >
          <div
            className={`
              tw-flex
              tw-min-w-0
              tw-items-center
              ${
                sidebarOpen
                  ? "tw-gap-3"
                  : "tw-justify-center"
              }
            `}
          >
            <div className="tw-flex tw-h-20 tw-w-20 tw-flex-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-lg ">
              <img
                src={logo}
                alt="iParking logo"
                width="40"
                height="40"
                decoding="async"
                draggable="false"
                className="tw-h-full tw-w-full tw-select-none tw-object-contain"
              />
            </div>

            {sidebarOpen && (
              <div className="tw-min-w-0">
                <h1 className="tw-truncate tw-text-base tw-font-semibold tw-text-white">
                  {organizationName}
                </h1>

                <p className="tw-truncate tw-text-xs tw-text-slate-500">
                  Удирдлагын систем
                </p>
              </div>
            )}
          </div>

          {sidebarOpen && (
            <button
              type="button"
              onClick={toggleSidebar}
              aria-label="Цэс хураах"
              className="tw-hidden tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-lg tw-text-slate-500 tw-transition-colors hover:tw-bg-slate-800 hover:tw-text-white md:tw-flex"
            >
              <FiChevronLeft size={20} />
            </button>
          )}
        </div>

        {/* COLLAPSED MENU BUTTON */}
        {!sidebarOpen && (
          <button
            type="button"
            onClick={toggleSidebar}
            aria-label="Цэс дэлгэх"
            className="tw-mx-auto tw-mt-3 tw-hidden tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-lg tw-text-slate-500 tw-transition-colors hover:tw-bg-slate-800 hover:tw-text-white md:tw-flex"
          >
            <FiMenu size={19} />
          </button>
        )}

        {/* NAVIGATION */}
        <nav className="custom-scrollbar tw-flex-1 tw-overflow-y-auto tw-overflow-x-hidden tw-p-3">
          {filteredMenu.length > 0 ? (
            <ul className="tw-space-y-1">
              {filteredMenu.map((item) => {
                const hasActiveSubItem =
                  item.subItems?.some(
                    (subItem) =>
                      isRouteActive(
                        subItem.url
                      )
                  );

                const isActive =
                  item.groupOnly
                    ? Boolean(
                        hasActiveSubItem
                      )
                    : isRouteActive(
                        resolveMenuUrl(
                          item
                        )
                      ) ||
                      Boolean(
                        hasActiveSubItem
                      );

                return (
                  <SidebarMenuItem
                    key={item.id}
                    item={item}
                    sidebarOpen={
                      sidebarOpen
                    }
                    isOpen={Boolean(
                      openMenus[item.id]
                    )}
                    isActive={isActive}
                    activeSubUrl={
                      activeSubUrl
                    }
                    onMenuClick={
                      handleMenuClick
                    }
                    onSubMenuClick={
                      handleSubMenuClick
                    }
                  />
                );
              })}
            </ul>
          ) : (
            sidebarOpen && (
              <div className="tw-mt-6 tw-rounded-lg tw-border tw-border-slate-800 tw-bg-slate-800/50 tw-p-4 tw-text-center">
                <p className="tw-text-sm tw-font-medium tw-text-slate-300">
                  Цэсийн эрх олдоогүй
                </p>

                <p className="tw-mt-1 tw-text-xs tw-leading-5 tw-text-slate-500">
                  Администраторт хандаж
                  эрхээ тохируулна уу.
                </p>
              </div>
            )
          )}
        </nav>

        {/* USER */}
        <div className="tw-flex-shrink-0 tw-border-t tw-border-slate-800 tw-p-3">
          <div
            className={`
              tw-flex
              tw-items-center
              tw-rounded-lg
              tw-bg-slate-800/70
              tw-p-2
              ${
                sidebarOpen
                  ? "tw-gap-3"
                  : "tw-justify-center"
              }
            `}
          >
            <div className="tw-flex tw-h-10 tw-w-10 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-blue-600 tw-text-sm tw-font-semibold tw-text-white">
              {userInitial}
            </div>

            {sidebarOpen && (
              <div className="tw-min-w-0 tw-flex-1">
                <p className="tw-truncate tw-text-sm tw-font-medium tw-text-slate-200">
                  {userName}
                </p>

                <p className="tw-truncate tw-text-xs tw-text-slate-500">
                  {roleName}
                </p>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <main
        className={`
          tw-flex
          tw-min-w-0
          tw-flex-1
          tw-flex-col
          tw-transition-[margin]
          tw-duration-300
          tw-ease-out
          ${
            sidebarOpen
              ? "md:tw-ml-64"
              : "md:tw-ml-20"
          }
        `}
      >
        {/* MOBILE HEADER */}
        <header className="tw-sticky tw-top-0 tw-z-30 tw-flex tw-h-16 tw-flex-shrink-0 tw-items-center tw-border-b tw-border-slate-200 tw-bg-white tw-px-4 md:tw-hidden">
          <button
            type="button"
            onClick={toggleSidebar}
            aria-label="Цэс нээх"
            className="tw-mr-3 tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-lg tw-text-slate-600 tw-transition-colors hover:tw-bg-slate-100"
          >
            <FiMenu size={22} />
          </button>

          <div className="tw-min-w-0 tw-flex-1">
            <p className="tw-text-xs tw-text-slate-500">
              {organizationName}
            </p>

            <h2 className="tw-truncate tw-text-base tw-font-semibold tw-text-slate-900">
              {currentPageName}
            </h2>
          </div>
        </header>

        {/* PAGE */}
        <div className="custom-scrollbar tw-flex-1 tw-overflow-x-hidden tw-overflow-y-auto">
          {children}
        </div>
      </main>
    </div>
  );
};

export default memo(DashboardLayout);
